import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockNfaLfaService } from '@/services/mock/MockNfaLfaService';
import { LetterForAward } from '@/types/domain';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Save, Award, Building2, Calendar, ShieldCheck, Truck } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const LfaEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [lfa, setLfa] = useState<LetterForAward | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form State
  const [title, setTitle] = useState('');
  const [awardedValue, setAwardedValue] = useState<number>(0);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [deliveryTerms, setDeliveryTerms] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  useEffect(() => {
    const loadLfa = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const data = await mockNfaLfaService.getLfaById(id);
        if (!data) {
          navigate(ROUTES.LFA.LIST);
          return;
        }
        setLfa(data);
        setTitle(data.title || '');
        setAwardedValue(data.awardedValue || 0);
        setPaymentTerms(data.paymentTerms || 'Net 30');
        setDeliveryTerms(data.deliveryTerms || 'DDP');
        setEffectiveDate(data.effectiveDate?.split('T')[0] || new Date().toISOString().split('T')[0]!);
        setExpiryDate(data.expiryDate?.split('T')[0] || '2027-12-31');
      } finally {
        setIsLoading(false);
      }
    };

    loadLfa();
  }, [id, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !lfa) return;

    try {
      setIsSaving(true);
      await mockNfaLfaService.updateLfa(id, {
        title,
        awardedValue: Number(awardedValue),
        paymentTerms,
        deliveryTerms,
        effectiveDate,
        expiryDate,
      });
      navigate(ROUTES.LFA.DETAILS(id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update Letter for Award.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Letter for Award Editor...
      </div>
    );
  }

  if (!lfa) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">LFA Record Not Found</h2>
        <Button size="sm" onClick={() => navigate(ROUTES.LFA.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to LFA Ledger
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.LFA.DETAILS(id!))}
            className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-primary px-2.5 py-0.5 bg-primary/10 rounded-lg border border-primary/20">
                {lfa.lfaNumber}
              </span>
              <h1 className="text-xl font-extrabold text-foreground tracking-tight font-poppins">
                Edit Letter for Award
              </h1>
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
              Awarded Vendor: <strong className="text-foreground">{lfa.vendorName}</strong> ({lfa.vendorCode}) • Linked NFA: <span className="font-mono font-bold text-primary">{lfa.nfaNumber}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.LFA.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-lfa-form"
            size="sm"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </div>

      <form id="edit-lfa-form" onSubmit={handleSave} className="space-y-6">
        {/* Section 1: Commercial Scope & Identification */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <Award className="h-4 w-4 text-primary" />
              Award Identification & Scope
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div>
              <label className="block font-bold text-foreground mb-1.5">
                Award Title / Contract Subject *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary font-poppins"
                placeholder="e.g. Letter for Award — Enterprise Server Hardware & Installation"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3.5 rounded-xl bg-muted/30 border border-border space-y-1">
                <span className="text-[10px] font-bold uppercase text-foreground-muted block">
                  Awarded Supplier (Selected Bidder)
                </span>
                <span className="font-bold text-foreground text-sm block flex items-center gap-1.5">
                  <Building2 className="h-4 w-4 text-primary" />
                  {lfa.vendorName}
                </span>
                <span className="font-mono text-xs text-foreground-secondary block">
                  Vendor ID: {lfa.vendorCode} • Quote ID: {lfa.quotationId}
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-primary/5 border border-primary/20 space-y-1">
                <span className="text-[10px] font-bold uppercase text-foreground-muted block">
                  Traceable Procurement Chain
                </span>
                <span className="font-mono text-xs font-bold text-primary block">
                  NFA: {lfa.nfaNumber} • RFQ: {lfa.rfqNumber}
                </span>
                <span className="font-mono text-[11px] text-foreground-secondary block">
                  Requisition: {lfa.requestNumber}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Financial Terms & Invoicing Milestones */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Agreed Commercial & Delivery Conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Awarded Commitment (₹ INR) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono font-bold text-foreground-muted">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={awardedValue}
                    onChange={(e) => setAwardedValue(Number(e.target.value))}
                    required
                    min={0}
                    step={1}
                    className="w-full rounded-xl border border-input bg-background pl-8 pr-3 py-2.5 text-xs font-bold text-foreground font-mono focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Payment Terms *
                </label>
                <select
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="Net 30">Net 30 Days Post-GRN</option>
                  <option value="Net 45">Net 45 Days Post-GRN</option>
                  <option value="Net 60">Net 60 Days Post-GRN</option>
                  <option value="20% Advance, 80% on Delivery">20% Advance, 80% on Delivery</option>
                  <option value="100% Against LC at Sight">100% Against LC at Sight</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Truck className="h-3.5 w-3.5 text-primary" /> Delivery Incoterms *
                </label>
                <select
                  value={deliveryTerms}
                  onChange={(e) => setDeliveryTerms(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="DDP">DDP — Delivered Duty Paid</option>
                  <option value="DAP">DAP — Delivered At Place</option>
                  <option value="FOB">FOB — Free On Board</option>
                  <option value="CIF">CIF — Cost, Insurance & Freight</option>
                  <option value="EXW">EXW — Ex Works (Plant Collection)</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Currency
                </label>
                <input
                  type="text"
                  value="INR (₹)"
                  disabled
                  className="w-full rounded-xl border border-input bg-muted p-2.5 text-xs font-bold font-mono text-foreground-muted"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-primary" /> Effective Award Date *
                </label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-rose-500" /> Award Acceptance Expiry Date *
                </label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground font-poppins"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Controls */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.LFA.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer px-6 shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};
